import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '@/lib/i18n';
import { getImageUrl } from '@/lib/api';
import { Search, MapPin, User, X } from 'lucide-react';

export default function SearchAutocomplete({ onClose }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useLang();
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/search/suggest?q=${encodeURIComponent(query)}`);
        const data = await res.json();
        setSuggestions(data.suggestions || []);
      } catch { setSuggestions([]); }
      setLoading(false);
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, API]);

  const handleSelect = (item) => {
    if (item.type === 'city') {
      navigate(`/miasta/${item.name}`);
    } else {
      navigate(`/modelki/${item.slug}`);
    }
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/modelki?search=${encodeURIComponent(query.trim())}`);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-start justify-center pt-20 px-4" onClick={onClose}>
      <div data-testid="search-autocomplete" className="w-full max-w-lg bg-[#18181B] border border-[#27272A] rounded-2xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <form onSubmit={handleSubmit} className="flex items-center gap-3 px-4 py-3 border-b border-[#27272A]">
          <Search className="w-5 h-5 text-[#52525B] shrink-0" />
          <input
            ref={inputRef}
            data-testid="search-input"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t("search.placeholder")}
            className="flex-1 bg-transparent text-white text-sm placeholder:text-[#52525B] focus:outline-none"
          />
          {query && (
            <button type="button" onClick={() => setQuery('')} className="text-[#52525B] hover:text-white">
              <X className="w-4 h-4" />
            </button>
          )}
        </form>

        {suggestions.length > 0 && (
          <div className="max-h-80 overflow-y-auto py-2">
            {suggestions.map((item, i) => (
              <button
                key={`${item.type}-${item.name}-${i}`}
                data-testid={`suggestion-${i}`}
                onClick={() => handleSelect(item)}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors text-left"
              >
                {item.type === 'city' ? (
                  <>
                    <div className="w-8 h-8 rounded-full bg-[#E11D48]/10 flex items-center justify-center shrink-0">
                      <MapPin className="w-4 h-4 text-[#E11D48]" />
                    </div>
                    <div>
                      <p className="text-sm text-white">{item.name}</p>
                      <p className="text-xs text-[#52525B]">{item.count} {t('catalog.models')}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-8 h-10 rounded bg-[#27272A] overflow-hidden shrink-0">
                      {item.photo ? (
                        <img src={getImageUrl(item.photo)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><User className="w-4 h-4 text-[#52525B]" /></div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{item.name}</p>
                      <p className="text-xs text-[#52525B]">{item.city} {item.priceHour ? `· ${item.priceHour} ${t('catalog.perHour')}` : ''}</p>
                    </div>
                  </>
                )}
              </button>
            ))}
          </div>
        )}

        {query.length >= 2 && suggestions.length === 0 && !loading && (
          <div className="px-4 py-6 text-center">
            <p className="text-sm text-[#52525B]">{t('search.noResults')}</p>
          </div>
        )}

        {loading && (
          <div className="px-4 py-4 text-center">
            <div className="w-5 h-5 border-2 border-[#E11D48] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        )}

        <div className="px-4 py-2 border-t border-[#27272A] flex items-center justify-between">
          <span className="text-[10px] text-[#52525B]">Enter - {t('nav.search').toLowerCase()} &middot; Esc - {t('common.close').toLowerCase()}</span>
        </div>
      </div>
    </div>
  );
}
